import React, { useState } from "react";
import { BadgeIndianRupee } from "lucide-react";
import { Sheet, SheetContent } from "./ui/sheet";
import { fmt } from "../lib/format";
import { boardOf, GROUPS } from "../lib/board";

const none = "இல்லை";

/* Client-format rate card: Singles / Doubles rows, then one block per 3D & 4D ticket with consolation tiers */
export function RateCardBody({ slats = [], rateMode = "customer" }) {
  const party = rateMode === "party";
  const rateOf = (s) => (party ? s.party_rate || s.rate : s.rate);
  const winOf = (s) => (party ? s.party_win_amount || s.win_amount : s.win_amount);
  const active = slats.filter((s) => s.is_active !== false);
  const byGroup = (id) => active.filter((s) => boardOf(s).group === id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const singles = byGroup("single"), doubles = byGroup("double"), d3 = byGroup("3d"), d4 = byGroup("4d");
  const rowVal = (list, pick) => { const v = [...new Set(list.map(pick))]; return v.length === 1 ? fmt(v[0]) : v.map(fmt).join(" / "); };
  return (
    <>
      <div className="space-y-2">
        {singles.length > 0 && <Block title="✅ SINGLES" lines={[["Rate", rowVal(singles, rateOf)], ["Winning", rowVal(singles, winOf)]]} testid="rate-card-singles" />}
        {doubles.length > 0 && <Block title="✅ DOUBLES" lines={[["Rate", rowVal(doubles, rateOf)], ["Winning", rowVal(doubles, winOf)]]} testid="rate-card-doubles" />}
        {d3.length > 0 && <Divider />}
        {d3.map((s) => <Block key={s.id} title={`🎟️ ${fmt(rateOf(s))} TICKET`} sub="3D" lines={[["Winning", fmt(winOf(s))], ["BC", s.cons_bc > 0 ? fmt(s.cons_bc) : none], ["C", s.cons_c > 0 ? fmt(s.cons_c) : none]]} testid={`rate-card-3d-${s.id}`} />)}
        {d4.length > 0 && <Divider />}
        {d4.map((s) => <Block key={s.id} title={`💥 ${fmt(rateOf(s))} TICKET`} sub="4D" lines={[["Winning", fmt(winOf(s))], ["ABC", s.cons_abc > 0 ? fmt(s.cons_abc) : none], ["BC", s.cons_bc > 0 ? fmt(s.cons_bc) : none], ["C", s.cons_c > 0 ? fmt(s.cons_c) : none]]} testid={`rate-card-4d-${s.id}`} />)}
      </div>
      <p className="text-[10px] text-slate-500 text-center mt-3">ABC = கடைசி 3 இலக்கம் · BC = கடைசி 2 · C = கடைசி 1 · ஆறுதல் பரிசு ticket-க்கு (box-க்கும் பொருந்தும்)</p>
    </>
  );
}

export function RateCardSheet({ slats = [], rateMode = "customer", isPartyUser = false, trigger }) {
  const [open, setOpen] = useState(false);
  const party = rateMode === "party";

  return (
    <>
      {trigger ? <span onClick={() => setOpen(true)}>{trigger}</span> : (
        <button onClick={() => setOpen(true)} className="text-xs font-bold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full flex items-center gap-1" data-testid="rate-card-btn">
          <BadgeIndianRupee className="w-3.5 h-3.5" />Rate & Winning
        </button>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rl-glass border-slate-200 rounded-t-3xl max-w-[480px] mx-auto max-h-[85vh] overflow-y-auto" data-testid="rate-card-sheet">
          <div className="text-center mb-3">
            <div className="font-display font-black text-lg text-slate-900">🔥 RATE & WINNING DETAILS 🔥</div>
            {isPartyUser && <div className={`inline-block mt-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full ${party ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`} data-testid="rate-card-mode">{party ? "PARTY RATE" : "CUSTOMER RATE"}</div>}
          </div>
          <RateCardBody slats={slats} rateMode={rateMode} />
        </SheetContent>
      </Sheet>
    </>
  );
}

const Divider = () => <div className="text-center text-slate-300 text-xs tracking-widest">━━━━━━━━━━━━━━</div>;

function Block({ title, sub, lines, testid }) {
  return (
    <div className="rl-card px-3 py-2" data-testid={testid}>
      <div className="font-bold text-sm text-slate-900 flex items-center gap-2">{title}{sub && <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 rounded">{sub}</span>}</div>
      <div className="grid grid-cols-2 gap-x-3 mt-0.5">
        {lines.map(([k, v]) => <div key={k} className="flex justify-between text-xs"><span className="text-slate-500">{k}:</span><span className={`font-bold ${v === none ? "text-slate-400" : k === "Winning" ? "text-emerald-700" : "text-slate-800"}`}>{v}</span></div>)}
      </div>
    </div>
  );
}
